/**
 * Money formatting shared by every web surface.
 *
 * Amounts in Flow carry their own currency (a client is billed in EUR, CHF or
 * GBP). Every screen that shows an amount formats it with the record's
 * currency through this module, never with a hardcoded symbol.
 */
import { z } from 'zod';

/** ISO 4217 codes Flow accepts on clients, hire orders and contracts. */
export const CURRENCY_CODES = [
  'EUR',
  'USD',
  'GBP',
  'CHF',
  'PLN',
  'SEK',
  'NOK',
  'DKK',
  'CZK',
  'HUF',
] as const;

export type CurrencyCode = (typeof CURRENCY_CODES)[number];

export const CurrencyCodeSchema = z.enum(CURRENCY_CODES);

export const DEFAULT_CURRENCY: CurrencyCode = 'EUR';

/**
 * Display symbol per supported currency. Kept explicit rather than read from
 * `Intl` because invoice PDFs print these exact strings (CHF is `Fr.`, where
 * `Intl` would give `CHF`), and a PDF must not change with the runtime's ICU.
 */
export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'Fr.',
  PLN: 'zł',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  CZK: 'Kč',
  HUF: 'Ft',
};

function normalizeCurrency(currency: string | null | undefined): string {
  return (currency || DEFAULT_CURRENCY).trim().toUpperCase();
}

function isCurrencyCode(code: string): code is CurrencyCode {
  return (CURRENCY_CODES as readonly string[]).includes(code);
}

/**
 * Symbol for a currency code. A missing currency is EUR, the column default.
 * An unknown code falls back to `Intl`'s narrow symbol, then to the code
 * itself, so an amount is never shown with another currency's symbol.
 */
export function currencySymbol(currency: string | null | undefined): string {
  const code = normalizeCurrency(currency);
  if (isCurrencyCode(code)) return CURRENCY_SYMBOLS[code];

  try {
    const part = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? code;
  } catch {
    return code;
  }
}

export interface FormatMoneyOptions {
  /** BCP 47 locale. Defaults to `en-US`. */
  locale?: string;
  /** Fixed number of fraction digits. Defaults to the currency's own. */
  decimals?: number;
  notation?: 'standard' | 'compact';
}

/**
 * Format an amount in its currency with `Intl.NumberFormat`.
 *
 * @example
 * formatMoney(1234.5, 'GBP') // "£1,234.50"
 * formatMoney(1234.5, 'EUR', { locale: 'de-DE' }) // "1.234,50 €"
 */
export function formatMoney(
  amount: number,
  currency: string | null | undefined,
  options: FormatMoneyOptions = {},
): string {
  const code = normalizeCurrency(currency);
  const { locale = 'en-US', decimals, notation } = options;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      ...(decimals !== undefined && {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
      ...(notation && { notation }),
    }).format(amount);
  } catch {
    // A code Intl rejects (not ISO 4217): show the code, never a wrong symbol.
    return `${code} ${amount.toFixed(decimals ?? 2)}`;
  }
}

/** Amount totals keyed by ISO currency code, e.g. `{ EUR: 1200, GBP: 300 }`. */
export type MoneyByCurrency = Record<string, number>;

/**
 * Add `amount` to the `currency` bucket of `totals`, in place. Amounts in
 * different currencies are never summed together.
 */
export function addMoney(
  totals: MoneyByCurrency,
  currency: string | null | undefined,
  amount: number | null | undefined,
): MoneyByCurrency {
  const code = normalizeCurrency(currency);
  totals[code] = (totals[code] ?? 0) + (amount ?? 0);
  return totals;
}

/** Sum rows per currency. */
export function sumByCurrency<T>(
  rows: readonly T[],
  getAmount: (row: T) => number | null | undefined,
  getCurrency: (row: T) => string | null | undefined,
): MoneyByCurrency {
  const totals: MoneyByCurrency = {};
  for (const row of rows) addMoney(totals, getCurrency(row), getAmount(row));
  return totals;
}

/**
 * Format per-currency totals as one string, largest first, joined by ` · `.
 * An empty total formats as zero in `emptyCurrency`.
 *
 * @example
 * formatMoneyTotals({ EUR: 1000, GBP: 250 }) // "€1,000.00 · £250.00"
 */
export function formatMoneyTotals(
  totals: MoneyByCurrency,
  options: FormatMoneyOptions & { emptyCurrency?: string } = {},
): string {
  const { emptyCurrency = DEFAULT_CURRENCY, ...formatOptions } = options;
  const entries = Object.entries(totals).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return formatMoney(0, emptyCurrency, formatOptions);
  return entries
    .map(([code, amount]) => formatMoney(amount, code, formatOptions))
    .join(' · ');
}
